<template>
  <div class="service-consulta">
    <p class="text-2xl mb-1">
      Consulta unitária
    </p>
    <p class="mb-4 grey--text text--darken-1">
      Consulte pessoa física ou jurídica pelos filtros disponíveis.
    </p>

    <v-row dense class="mb-4">
      <v-col
        v-for="option in pessoaTipoOptions"
        :key="option.value"
        cols="12"
        sm="6"
      >
        <v-card
          outlined
          class="option-card"
          :class="{ 'option-card--selected': pessoaTipo === option.value }"
          @click="selectPessoaTipo(option.value)"
        >
          <v-list-item class="py-3">
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold d-flex align-center">
                {{ option.title }}
                <v-chip
                  class="ms-2 consulta-tipo-chip"
                  :class="option.value === 'pj' ? 'consulta-tipo-chip--pj' : 'consulta-tipo-chip--pf'"
                  x-small
                  label
                  outlined
                >
                  {{ option.chip }}
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap">
                {{ option.subtitle }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text class="pt-5">
        <v-row align="start">
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="searchType"
              :items="searchTypes"
              item-text="label"
              item-value="value"
              label="Tipo de consulta"
              outlined
              dense
              hide-details
              @change="clearResult"
            />
          </v-col>

          <!-- PF: CPF -->
          <template v-if="pessoaTipo === 'pf' && searchType === 'cpf'">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-text-field
                v-model="form.cpf"
                label="CPF"
                outlined
                dense
                hide-details
                maxlength="14"
                placeholder="000.000.000-00"
              />
            </v-col>
          </template>

          <!-- PJ: CNPJ -->
          <template v-else-if="pessoaTipo === 'pj' && searchType === 'cnpj'">
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-text-field
                v-model="form.cnpj"
                label="CNPJ"
                outlined
                dense
                hide-details
                maxlength="18"
                placeholder="00.000.000/0000-00"
              />
            </v-col>
          </template>

          <!-- PJ: Razão social -->
          <template v-else-if="pessoaTipo === 'pj' && searchType === 'razaoSocial'">
            <v-col
              cols="12"
              sm="6"
              md="5"
            >
              <v-text-field
                v-model="form.razaoSocial"
                label="Razão social"
                outlined
                dense
                hide-details
                placeholder="Nome da empresa"
              />
            </v-col>
          </template>

          <!-- E-mail (PF / PJ) -->
          <template v-else-if="searchType === 'email'">
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-text-field
                v-model="form.email"
                label="E-mail"
                outlined
                dense
                hide-details
                type="email"
              />
            </v-col>
            <template v-if="pessoaTipo === 'pf'">
              <v-col
                cols="6"
                sm="3"
                md="2"
              >
                <v-text-field
                  v-model.number="form.idadeinicial"
                  label="Idade inicial"
                  outlined
                  dense
                  hide-details
                  type="number"
                />
              </v-col>
              <v-col
                cols="6"
                sm="3"
                md="2"
              >
                <v-text-field
                  v-model.number="form.idadefinal"
                  label="Idade final"
                  outlined
                  dense
                  hide-details
                  type="number"
                />
              </v-col>
            </template>
          </template>

          <!-- Placa (somente PF) -->
          <template v-else-if="pessoaTipo === 'pf' && searchType === 'placa'">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-text-field
                v-model="form.placa"
                label="Placa"
                outlined
                dense
                hide-details
                maxlength="8"
              />
            </v-col>
          </template>

          <!-- CEP (PF / PJ) -->
          <template v-else-if="searchType === 'cep'">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-text-field
                v-model="form.cep"
                label="CEP"
                outlined
                dense
                hide-details
                maxlength="9"
              />
            </v-col>
            <template v-if="pessoaTipo === 'pf'">
              <v-col
                cols="6"
                sm="3"
                md="2"
              >
                <v-text-field
                  v-model.number="form.idadeinicial"
                  label="Idade inicial"
                  outlined
                  dense
                  hide-details
                  type="number"
                />
              </v-col>
              <v-col
                cols="6"
                sm="3"
                md="2"
              >
                <v-text-field
                  v-model.number="form.idadefinal"
                  label="Idade final"
                  outlined
                  dense
                  hide-details
                  type="number"
                />
              </v-col>
            </template>
          </template>

          <!-- Telefone (PF / PJ) -->
          <template v-else-if="searchType === 'telefone'">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-text-field
                v-model="form.telefone"
                label="Telefone (DDD + número)"
                outlined
                dense
                hide-details
                placeholder="11999999999"
              />
            </v-col>
          </template>

          <!-- Nome / Nome da mãe (somente PF) -->
          <template v-else-if="pessoaTipo === 'pf' && (searchType === 'nome' || searchType === 'nomeMae')">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-text-field
                v-model="form.nome"
                :label="searchType === 'nomeMae' ? 'Nome da mãe' : 'Nome'"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="6"
              sm="3"
              md="1"
            >
              <v-text-field
                v-model.number="form.idadeinicial"
                label="Idade inicial"
                outlined
                dense
                hide-details
                type="number"
              />
            </v-col>
            <v-col
              cols="6"
              sm="3"
              md="1"
            >
              <v-text-field
                v-model.number="form.idadefinal"
                label="Idade final"
                outlined
                dense
                hide-details
                type="number"
              />
            </v-col>
            <v-col
              cols="8"
              sm="6"
              md="2"
            >
              <v-text-field
                v-model="form.cidade"
                label="Cidade"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col
              cols="4"
              sm="3"
              md="1"
            >
              <v-text-field
                v-model="form.uf"
                label="UF"
                outlined
                dense
                hide-details
                maxlength="2"
              />
            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          color="primary"
          class="me-3"
          :loading="loading"
          @click="search"
        >
          Consultar
        </v-btn>
        <v-btn
          color="secondary"
          outlined
          @click="resetForm"
        >
          Limpar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      v-if="resultRows.length"
      class="mt-4"
    >
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Resultados ({{ resultRows.length }})</span>
        <v-btn
          small
          text
          color="primary"
          @click="showRaw = !showRaw"
        >
          {{ showRaw ? 'Ocultar JSON' : 'Ver JSON' }}
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="normalizedRows"
          :items-per-page="10"
          class="elevation-0"
          dense
        >
          <template
            v-if="pessoaTipo === 'pj' && searchType !== 'cnpj'"
            #item.actions="{ item }"
          >
            <v-btn
              v-if="item._cnpj"
              x-small
              outlined
              color="primary"
              :loading="loadingCnpj === item._cnpj"
              @click="consultarDetalheCnpj(item._cnpj)"
            >
              Ver CNPJ
            </v-btn>
            <span
              v-else
              class="grey--text text-caption"
            >—</span>
          </template>
        </v-data-table>

        <v-expansion-panels
          v-if="resultRows.length"
          class="mt-4"
          accordion
        >
          <v-expansion-panel
            v-for="(row, index) in resultRows"
            :key="index"
          >
            <v-expansion-panel-header>
              Detalhes — {{ rowTitle(row, index) }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense>
                <tbody>
                  <tr
                    v-for="(value, key) in flattenRow(row)"
                    :key="key"
                  >
                    <td class="font-weight-medium">
                      {{ key }}
                    </td>
                    <td>{{ formatValue(value) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <pre
          v-if="showRaw"
          class="mt-4 pa-3 result-raw rounded"
          style="max-height: 360px; overflow: auto; white-space: pre-wrap;"
        >{{ JSON.stringify(rawResult, null, 2) }}</pre>
      </v-card-text>
    </v-card>

    <v-alert
      v-else-if="searched && !loading"
      class="mt-4"
      type="info"
      text
    >
      Nenhum resultado encontrado para os filtros informados.
    </v-alert>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import {
  consultarPorCpf,
  consultarPorEmail,
  consultarPorPlaca,
  consultarPorCep,
  consultarPorNome,
  consultarPorNomeMae,
  consultarPorTelefone,
} from '@/services/consultaPf'
import {
  consultarPjPorCnpj,
  consultarPjPorEmail,
  consultarPjPorCep,
  consultarPjPorTelefone,
  consultarPjPorRazaoSocial,
} from '@/services/consultaPj'

const emptyForm = () => ({
  cpf: '',
  cnpj: '',
  razaoSocial: '',
  email: '',
  placa: '',
  cep: '',
  telefone: '',
  nome: '',
  idadeinicial: null,
  idadefinal: null,
  cidade: '',
  uf: '',
})

const SEARCH_TYPES_PF = [
  { label: 'CPF', value: 'cpf' },
  { label: 'E-mail', value: 'email' },
  { label: 'Telefone', value: 'telefone' },
  { label: 'Nome', value: 'nome' },
  { label: 'Nome da mãe', value: 'nomeMae' },
  { label: 'CEP', value: 'cep' },
  { label: 'Placa', value: 'placa' },
]

const SEARCH_TYPES_PJ = [
  { label: 'CNPJ', value: 'cnpj' },
  { label: 'Razão social', value: 'razaoSocial' },
  { label: 'E-mail', value: 'email' },
  { label: 'Telefone', value: 'telefone' },
  { label: 'CEP', value: 'cep' },
]

const HEADERS_PF = [
  { text: 'Nome', value: 'Nome' },
  { text: 'CPF', value: 'Doc' },
  { text: 'Nascimento', value: 'DataNascimento' },
  { text: 'Gênero', value: 'Genero' },
  { text: 'Cidade', value: 'Cidade' },
  { text: 'UF', value: 'UF' },
]

const HEADERS_PJ_DETAIL = [
  { text: 'Razão social', value: 'RazaoSocial' },
  { text: 'CNPJ', value: 'Cnpj' },
  { text: 'Nome fantasia', value: 'NomeFantasia' },
  { text: 'Situação', value: 'SituacaoEmpresa' },
  { text: 'Cidade', value: 'Cidade' },
  { text: 'UF', value: 'UF' },
]

const HEADERS_PJ_LIST = [
  { text: 'Razão social', value: 'RazaoSocial' },
  { text: 'CNPJ', value: 'Cnpj' },
  { text: 'Cidade', value: 'Cidade' },
  { text: 'E-mail', value: 'Email' },
  { text: 'Ações', value: 'actions', sortable: false },
]

export default {
  setup() {
    const pessoaTipo = ref('pf')
    const searchType = ref('cpf')
    const form = ref(emptyForm())
    const loading = ref(false)
    const loadingCnpj = ref(null)
    const searched = ref(false)
    const rawResult = ref(null)
    const showRaw = ref(false)

    const searchTypes = computed(() => (
      pessoaTipo.value === 'pj' ? SEARCH_TYPES_PJ : SEARCH_TYPES_PF
    ))

    return {
      pessoaTipo,
      searchType,
      form,
      loading,
      loadingCnpj,
      searched,
      rawResult,
      showRaw,
      searchTypes,
      pessoaTipoOptions: [
        {
          value: 'pf',
          title: 'Pessoa Física',
          chip: 'PF',
          subtitle: 'CPF, nome, e-mail, telefone, CEP, placa e nome da mãe.',
        },
        {
          value: 'pj',
          title: 'Pessoa Jurídica',
          chip: 'PJ',
          subtitle: 'CNPJ, razão social, e-mail, telefone e CEP.',
        },
      ],
    }
  },

  computed: {
    resultRows() {
      const data = this.rawResult
      if (!data) return []

      if (Array.isArray(data.Cadastro)) return data.Cadastro
      if (Array.isArray(data.results)) return data.results
      if (Array.isArray(data)) return data
      if (data.Cadastro && typeof data.Cadastro === 'object') return [data.Cadastro]
      if (typeof data === 'object') return [data]
      return []
    },

    isPjListResult() {
      return this.pessoaTipo === 'pj' && this.searchType !== 'cnpj'
    },

    tableHeaders() {
      if (this.pessoaTipo === 'pf') return HEADERS_PF
      if (this.isPjListResult) return HEADERS_PJ_LIST
      return HEADERS_PJ_DETAIL
    },

    normalizedRows() {
      return this.resultRows.map(row => {
        const cnpj = this.extractCnpj(row)
        return {
          ...row,
          Cnpj: row.Cnpj || row.CNPJ || row.cnpj || cnpj || '',
          RazaoSocial: row.RazaoSocial || row.razaoSocial || row.Nome || '',
          _cnpj: cnpj,
        }
      })
    },
  },

  methods: {
    selectPessoaTipo(tipo) {
      if (this.pessoaTipo === tipo) return
      this.pessoaTipo = tipo
      this.searchType = tipo === 'pj' ? 'cnpj' : 'cpf'
      this.form = emptyForm()
      this.clearResult()
    },

    clearResult() {
      this.rawResult = null
      this.searched = false
      this.showRaw = false
      this.loadingCnpj = null
    },

    resetForm() {
      this.form = emptyForm()
      this.clearResult()
    },

    extractCnpj(row) {
      const raw = row?.Cnpj ?? row?.CNPJ ?? row?.cnpj ?? ''
      const digits = String(raw).replace(/\D/g, '')
      return digits.length === 14 ? digits : ''
    },

    rowTitle(row, index) {
      return (
        row.RazaoSocial
        || row.razaoSocial
        || row.Nome
        || row.nome
        || row.Cnpj
        || row.CNPJ
        || `Registro ${index + 1}`
      )
    },

    flattenRow(row) {
      const flat = {}
      Object.keys(row || {}).forEach(key => {
        if (key === '_cnpj') return
        const value = row[key]
        if (value !== null && typeof value === 'object') {
          flat[key] = JSON.stringify(value)
        } else {
          flat[key] = value
        }
      })
      return flat
    },

    formatValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      return value
    },

    extractErrorMessage(error) {
      return (
        error?.response?.data?.message
        || error?.response?.data?.error
        || error?.response?.data?.Retorno
        || error?.message
        || (this.pessoaTipo === 'pj'
          ? 'Falha ao consultar pessoa jurídica.'
          : 'Falha ao consultar pessoa física.')
      )
    },

    buildPayload() {
      const f = this.form

      if (this.pessoaTipo === 'pj') {
        if (this.searchType === 'cnpj') {
          return { cnpj: String(f.cnpj || '').replace(/\D/g, '') }
        }
        if (this.searchType === 'razaoSocial') {
          return { RazaoSocial: String(f.razaoSocial || '').trim() }
        }
        if (this.searchType === 'email') {
          return { email: String(f.email || '').trim() }
        }
        if (this.searchType === 'cep') {
          return { cep: String(f.cep || '').replace(/\D/g, '') }
        }
        if (this.searchType === 'telefone') {
          return { telefone: String(f.telefone || '').replace(/\D/g, '') }
        }
        return {}
      }

      if (this.searchType === 'cpf') {
        return { cpf: String(f.cpf || '').replace(/\D/g, '') }
      }
      if (this.searchType === 'email') {
        return {
          email: f.email,
          ...(f.idadeinicial != null && f.idadeinicial !== ''
            ? { idadeinicial: Number(f.idadeinicial) }
            : {}),
          ...(f.idadefinal != null && f.idadefinal !== ''
            ? { idadefinal: Number(f.idadefinal) }
            : {}),
        }
      }
      if (this.searchType === 'placa') {
        return { placa: String(f.placa || '').trim().toUpperCase() }
      }
      if (this.searchType === 'cep') {
        return {
          cep: String(f.cep || '').replace(/\D/g, ''),
          ...(f.idadeinicial != null && f.idadeinicial !== ''
            ? { idadeinicial: Number(f.idadeinicial) }
            : {}),
          ...(f.idadefinal != null && f.idadefinal !== ''
            ? { idadefinal: Number(f.idadefinal) }
            : {}),
        }
      }
      if (this.searchType === 'telefone') {
        return { telefone: String(f.telefone || '').replace(/\D/g, '') }
      }
      return {
        nome: String(f.nome || '').trim(),
        ...(f.idadeinicial != null && f.idadeinicial !== ''
          ? { idadeinicial: Number(f.idadeinicial) }
          : {}),
        ...(f.idadefinal != null && f.idadefinal !== ''
          ? { idadefinal: Number(f.idadefinal) }
          : {}),
        ...(f.cidade ? { cidade: f.cidade } : {}),
        ...(f.uf ? { uf: String(f.uf).toUpperCase() } : {}),
      }
    },

    validatePayload(payload) {
      if (this.pessoaTipo === 'pj') {
        if (this.searchType === 'cnpj' && (!payload.cnpj || payload.cnpj.length !== 14)) {
          return 'Informe um CNPJ válido (14 dígitos).'
        }
        if (this.searchType === 'razaoSocial' && !payload.RazaoSocial) {
          return 'Informe a razão social.'
        }
        if (this.searchType === 'email' && !payload.email) {
          return 'Informe um e-mail.'
        }
        if (this.searchType === 'cep' && !payload.cep) {
          return 'Informe o CEP.'
        }
        if (this.searchType === 'telefone' && !payload.telefone) {
          return 'Informe o telefone.'
        }
        return null
      }

      if (this.searchType === 'cpf' && (!payload.cpf || payload.cpf.length < 11)) {
        return 'Informe um CPF válido.'
      }
      if (this.searchType === 'email' && !payload.email) {
        return 'Informe um e-mail.'
      }
      if (this.searchType === 'placa' && !payload.placa) {
        return 'Informe a placa.'
      }
      if (this.searchType === 'cep' && !payload.cep) {
        return 'Informe o CEP.'
      }
      if (this.searchType === 'telefone' && !payload.telefone) {
        return 'Informe o telefone.'
      }
      if (
        (this.searchType === 'nome' || this.searchType === 'nomeMae')
        && !payload.nome
      ) {
        return 'Informe o nome.'
      }
      return null
    },

    async consultarDetalheCnpj(cnpj) {
      if (!cnpj || this.loadingCnpj) return
      this.loadingCnpj = cnpj
      this.$loading(true)
      try {
        const data = await consultarPjPorCnpj(cnpj)
        this.rawResult = data
        this.searchType = 'cnpj'
        this.form.cnpj = cnpj
        this.searched = true
        if (!this.resultRows.length) {
          this.$notify({
            type: 'info',
            text: 'CNPJ consultado sem cadastro detalhado.',
          })
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.loadingCnpj = null
        this.$loading(false)
      }
    },

    async search() {
      const payload = this.buildPayload()
      const validationError = this.validatePayload(payload)
      if (validationError) {
        this.$notify({ type: 'error', text: validationError })
        return
      }

      this.loading = true
      this.clearResult()
      this.$loading(true)

      try {
        let data

        if (this.pessoaTipo === 'pj') {
          switch (this.searchType) {
            case 'cnpj':
              data = await consultarPjPorCnpj(payload.cnpj)
              break
            case 'razaoSocial':
              data = await consultarPjPorRazaoSocial(payload)
              break
            case 'email':
              data = await consultarPjPorEmail(payload)
              break
            case 'cep':
              data = await consultarPjPorCep(payload)
              break
            case 'telefone':
              data = await consultarPjPorTelefone(payload)
              break
            default:
              throw new Error('Tipo de consulta PJ inválido.')
          }
        } else {
          switch (this.searchType) {
            case 'cpf':
              data = await consultarPorCpf(payload.cpf)
              break
            case 'email':
              data = await consultarPorEmail(payload)
              break
            case 'placa':
              data = await consultarPorPlaca(payload)
              break
            case 'cep':
              data = await consultarPorCep(payload)
              break
            case 'telefone':
              data = await consultarPorTelefone(payload)
              break
            case 'nomeMae':
              data = await consultarPorNomeMae(payload)
              break
            default:
              data = await consultarPorNome(payload)
          }
        }

        this.rawResult = data
        this.searched = true

        console.log('[Consulta]', this.pessoaTipo, this.searchType, { payload, data })

        if (!this.resultRows.length) {
          this.$notify({
            type: 'info',
            text: 'Consulta concluída sem resultados.',
          })
        }
      } catch (error) {
        this.searched = true
        this.$notify({
          type: 'error',
          text: this.extractErrorMessage(error),
        })
      } finally {
        this.loading = false
        this.$loading(false)
      }
    },
  },
}
</script>

<style scoped>
.option-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.option-card:hover,
.option-card--selected {
  border-color: #271540 !important;
  background-color: rgba(39, 21, 64, 0.06) !important;
}

.consulta-tipo-chip {
  background-color: transparent !important;
  font-weight: 600;
}

.consulta-tipo-chip--pf {
  border-color: #4caf50 !important;
  color: #4caf50 !important;
}

.consulta-tipo-chip--pj {
  border-color: #9155fd !important;
  color: #9155fd !important;
}

.result-raw {
  background-color: #eceff1;
}

.theme--dark .option-card {
  border-color: rgba(255, 255, 255, 0.22) !important;
}

.theme--dark .option-card:hover,
.theme--dark .option-card--selected {
  border-color: #c5b3ff !important;
  background-color: rgba(197, 179, 255, 0.12) !important;
}

.theme--dark .consulta-tipo-chip--pf {
  border-color: #81c784 !important;
  color: #81c784 !important;
}

.theme--dark .consulta-tipo-chip--pj {
  border-color: #b794ff !important;
  color: #b794ff !important;
}

.theme--dark .result-raw {
  background-color: #3c4043;
  color: #e8eaed;
}
</style>
